<template>
  <div class="customer-sync-mapping-tab">
    <div class="field-mapping-container">
      <div class="section-header">
        <h3>Contact Sync Options</h3>
      </div>
      
      <div v-if="loading" class="loading-state">
        <p>Loading field mappings...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadData" class="btn-retry">Retry</button>
      </div>
      
      <div v-else>
        <!-- Contact Mapping Section -->
        <div class="mapping-section">
          <div class="section-title">
            <h4>Contact Field Mapping</h4>
            <p class="section-subtitle">Map ServiceM8 Contact fields (from companycontact.json) to {{ brand.agencyName }} contact fields</p>
          </div>
          
          <!-- Contact Example Selector -->
          <div class="example-selector">
            <label class="example-label">Example Contact:</label>
            <div class="example-selector-controls">
              <select 
                v-model="selectedContactId" 
                @change="loadExampleContact"
                class="contact-select"
              >
                <option value="">-- Select a contact --</option>
                <option 
                  v-for="contact in displayedContacts" 
                  :key="contact.id" 
                  :value="contact.id"
                >
                  {{ formatContactDisplayName(contact) }}
                </option>
                <option 
                  v-if="showMoreContactsOption" 
                  value="__more__"
                  class="more-option"
                >
                  ▼ More ({{ remainingContactsCount }})
                </option>
                <option 
                  v-if="!isSearchMode" 
                  value="__search__"
                  class="search-option"
                >
                  🔍 Search contacts...
                </option>
              </select>
              <div v-if="isSearchMode" class="search-input-container">
                <input 
                  v-model="contactSearchTerm" 
                  @input="searchContacts"
                  type="text" 
                  placeholder="Search by name, email, or phone..." 
                  class="contact-search-input"
                />
                <button 
                  @click="exitSearchMode" 
                  class="btn-exit-search"
                >
                  Cancel
                </button>
              </div>
              <button 
                v-if="selectedContactId" 
                @click="clearExampleContact" 
                class="btn-clear-example"
              >
                Clear
              </button>
            </div>
          </div>
          
          <div class="mapping-table-container">
            <div class="table-header">
              <div class="table-header-left">
                <span class="field-count">{{ filteredContactFields.length }} field(s)</span>
              </div>
              <div class="table-header-right">
                <input 
                  v-model="contactSearchQuery" 
                  type="text" 
                  placeholder="Search contact fields..." 
                  class="search-input"
                />
                <button @click="loadData(true)" class="btn-refresh" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
              </div>
      </div>

            <div v-if="filteredContactFields.length === 0" class="no-data">
              <p>No contact fields found.</p>
      </div>
            
            <table v-else class="mapping-table">
          <thead>
            <tr>
                  <th>ServiceM8 Contact Field</th>
                  <th>Sync Direction</th>
                  <th>{{ brand.agencyName }} Field</th>
                  <th v-if="selectedExampleContact">Example Value</th>
            </tr>
          </thead>
          <tbody>
                <tr v-for="field in filteredContactFields" :key="field.key">
                  <td class="field-cell">
                    <div class="field-info">
                      <span class="field-name">{{ field.name }}</span>
                      <span 
                        :class="['field-type-badge', `field-type-${field.type}`]"
                        :title="getFieldTooltip(field.key)"
                      >
                        {{ getFieldBadgeLabel(field.type) }}
                      </span>
                    </div>
              </td>
                  <td class="sync-direction-cell">
                    <span class="sync-arrow" title="Sync flows both ways">↔</span>
                  </td>
                  <td class="ghl-field-cell">
                    <select 
                      :value="contactMappings[field.key] || ''"
                      @change="handleFieldChange(field.key, $event, 'contact')"
                      class="ghl-field-select"
                    >
                      <option value="">Ignore</option>
                      <!-- Default Fields optgroup for 'type' (Role) field -->
                      <optgroup v-if="field.key === 'type'" label="ServiceM8 Role ({{ brand.agencyName }} → ServiceM8 default)">
                        <option value="Job">Job</option>
                        <option value="Billing">Billing</option>
                        <option value="Property Manager">Property Manager</option>
                      </optgroup>
                      <optgroup label="Standard Fields" v-if="standardGHLFields.length > 0">
                        <option 
                          v-for="ghlField in standardGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup label="ServiceM8 Formatted Fields" v-if="formattedGHLFields.length > 0">
                        <option 
                          v-for="ghlField in formattedGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup label="Contact Custom Fields" v-if="contactCustomGHLFields.length > 0">
                        <option 
                          v-for="ghlField in contactCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup label="Company Custom Fields" v-if="companyCustomGHLFields.length > 0">
                        <option 
                          v-for="ghlField in companyCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                    </select>
                  </td>
                  <td v-if="selectedExampleContact" class="example-value-cell">
                    <span class="example-value">{{ getExampleValue(field.key) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Company Mapping Section -->
        <div class="mapping-section">
          <div class="section-title">
            <h4>Company Field Mapping</h4>
          </div>
          
          <div class="mapping-table-container">
            <div class="table-header">
              <div class="table-header-left">
                <span class="field-count">{{ filteredCompanyFields.length }} field(s)</span>
              </div>
              <div class="table-header-right">
                <input 
                  v-model="companySearchQuery" 
                  type="text" 
                  placeholder="Search company fields..." 
                  class="search-input"
                />
                <button @click="loadData(true)" class="btn-refresh" :disabled="loading">
                  {{ loading ? 'Loading...' : 'Refresh' }}
                  </button>
              </div>
            </div>
            
            <div v-if="filteredCompanyFields.length === 0" class="no-data">
              <p>No company fields found.</p>
            </div>
            
            <table v-else class="mapping-table">
              <thead>
                <tr>
                  <th>ServiceM8 Client Field</th>
                  <th>Sync Direction</th>
                  <th>{{ brand.agencyName }} Field</th>
                  <th v-if="selectedExampleContact">Example Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in filteredCompanyFields" :key="field.key">
                  <td class="field-cell">
                    <div class="field-info">
                      <span class="field-name">{{ field.name }}</span>
                      <span :class="['field-type-badge', `field-type-${field.type}`]">
                        {{ field.type === 'standard' ? 'Standard' : 'Custom' }}
                      </span>
                </div>
              </td>
                  <td class="sync-direction-cell">
                    <span class="sync-arrow" title="Sync flows both ways">↔</span>
                  </td>
                  <td class="ghl-field-cell">
                    <select 
                      :value="companyMappings[field.key] || ''"
                      @change="handleFieldChange(field.key, $event, 'company')"
                      :disabled="isFieldDisabled(field.key, 'company')"
                      :title="getFieldDisabledTooltip(field.key, 'company')"
                      class="ghl-field-select"
                      :class="{ 'field-disabled': isFieldDisabled(field.key, 'company') }"
                    >
                      <option value="">Ignore</option>
                      <!-- Special dropdown for billing_attention field -->
                      <option v-if="field.key === 'billing_attention'" value="0">Billing Contact</option>
                      <option v-if="field.key === 'billing_attention'" value="1">Accounts Payable</option>
                      <option v-if="field.key === 'billing_attention'" value="2">Property Owner, C/- Company Name</option>
                      <optgroup v-if="field.key !== 'billing_attention' && standardGHLFields.length > 0" label="Standard Fields">
                        <option 
                          v-for="ghlField in standardGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="field.key !== 'billing_attention' && formattedGHLFields.length > 0" label="ServiceM8 Formatted Fields">
                        <option 
                          v-for="ghlField in formattedGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="field.key !== 'billing_attention' && contactCustomGHLFields.length > 0" label="Contact Custom Fields">
                        <option 
                          v-for="ghlField in contactCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="field.key !== 'billing_attention' && companyCustomGHLFields.length > 0" label="Company Custom Fields">
                        <option 
                          v-for="ghlField in companyCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                    </select>
                  </td>
                  <td v-if="selectedExampleContact" class="example-value-cell">
                    <span class="example-value">{{ getExampleValue(field.key, 'company') }}</span>
                  </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

        <!-- Company Custom Field Mapping Section -->
        <div class="mapping-section">
          <div class="section-title">
            <h4>Company Custom Field Mapping</h4>
          </div>
          
          <div class="mapping-table-container">
            <div class="table-header">
              <div class="table-header-left">
                <span class="field-count">{{ filteredCompanyCustomFields.length }} custom field(s)</span>
              </div>
              <div class="table-header-right">
                <input 
                  v-model="companyCustomFieldSearchQuery" 
                  type="text" 
                  placeholder="Search custom fields..." 
                  class="search-input"
                />
                <button @click="loadData(true)" class="btn-refresh" :disabled="loading">
                  {{ loading ? 'Loading...' : 'Refresh' }}
                  </button>
              </div>
            </div>
            
            <div v-if="filteredCompanyCustomFields.length === 0" class="no-data">
              <p>No custom fields found.</p>
            </div>
            
            <table v-else class="mapping-table">
              <thead>
                <tr>
                  <th>ServiceM8 Custom Field</th>
                  <th>Sync Direction</th>
                  <th>{{ brand.agencyName }} Field</th>
                  <th v-if="selectedExampleContact">Example Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in filteredCompanyCustomFields" :key="field.key">
                  <td class="field-cell">
                    <div class="field-info">
                      <span class="field-name">{{ field.name }}</span>
                      <span class="field-type-badge field-type-custom">Custom</span>
                    </div>
                  </td>
                  <td class="sync-direction-cell">
                    <span class="sync-arrow" title="Sync flows both ways">↔</span>
                  </td>
                  <td class="ghl-field-cell">
                    <select 
                      :value="companyMappings[field.key] || ''"
                      @change="handleFieldChange(field.key, $event, 'company')"
                      class="ghl-field-select"
                    >
                      <option value="">Ignore</option>
                      <optgroup v-if="standardGHLFields.length > 0" label="Standard Fields">
                        <option 
                          v-for="ghlField in standardGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="formattedGHLFields.length > 0" label="ServiceM8 Formatted Fields">
                        <option 
                          v-for="ghlField in formattedGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="contactCustomGHLFields.length > 0" label="Contact Custom Fields">
                        <option 
                          v-for="ghlField in contactCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                      <optgroup v-if="companyCustomGHLFields.length > 0" label="Company Custom Fields">
                        <option 
                          v-for="ghlField in companyCustomGHLFields" 
                          :key="ghlField.key" 
                          :value="ghlField.key"
                        >
                          {{ ghlField.name }}
                        </option>
                      </optgroup>
                    </select>
                  </td>
                  <td v-if="selectedExampleContact" class="example-value-cell">
                    <span class="example-value">{{ getExampleValue(field.key, 'company') }}</span>
                  </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

      </div>
    </div>
  </div>
</template>

<script>
import { brandMixin } from '../brandMixin'

export default {
  name: 'CustomerSyncMappingTab',
  mixins: [brandMixin],
  data() {
    return {
      companyFields: [],
      contactFields: [],
      ghlFields: [],
      companyMappings: {},
      contactMappings: {},
      loading: false,
      error: null,
      companySearchQuery: '',
      companyCustomFieldSearchQuery: '',
      contactSearchQuery: '',
      cacheKey: 'servicem8_company_fields_cache_v1',
      cacheExpiry: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
      // Contact example selector
      contactSearchTerm: '',
      contactSearchResults: [],
      allContacts: [], // All loaded contacts (up to 20)
      displayedContactsCount: 5, // Number of contacts to show initially
      selectedContactId: '',
      selectedExampleContact: null,
      searchTimeout: null,
      isSearchMode: false,
      // Contact custom field to tag mapping
    }
  },
  computed: {
    filteredCompanyFields() {
      const standardFields = this.companyFields.filter(f => f.type === 'standard');
      if (!this.companySearchQuery) {
        return standardFields;
      }
      const query = this.companySearchQuery.toLowerCase();
      return standardFields.filter(field => 
        field.name.toLowerCase().includes(query) ||
        field.key.toLowerCase().includes(query)
      );
    },
    filteredCompanyCustomFields() {
      const customFields = this.companyFields.filter(f => f.type === 'custom');
      if (!this.companyCustomFieldSearchQuery) {
        return customFields;
      }
      const query = this.companyCustomFieldSearchQuery.toLowerCase();
      return customFields.filter(field => 
        field.name.toLowerCase().includes(query) ||
        field.key.toLowerCase().includes(query)
      );
    },
    filteredContactFields() {
      // Show all fields (including is_primary_contact, location_type, head_office)
      if (!this.contactSearchQuery) {
        return this.contactFields;
      }
      const query = this.contactSearchQuery.toLowerCase();
      return this.contactFields.filter(field => 
        field.name.toLowerCase().includes(query) ||
        field.key.toLowerCase().includes(query)
      );
    },
    standardGHLFields() {
      return this.ghlFields.filter(f => f.type === 'standard');
    },
    formattedGHLFields() {
      return this.ghlFields.filter(f => f.type === 'formatted');
    },
    contactCustomGHLFields() {
      return this.ghlFields.filter(f => f.type === 'custom' && (!f.customFieldType || f.customFieldType === 'contact'));
    },
    companyCustomGHLFields() {
      return this.ghlFields.filter(f => f.type === 'custom' && f.customFieldType === 'company');
    },
    displayedContacts() {
      if (this.isSearchMode) {
        return this.contactSearchResults;
      }
      return this.allContacts.slice(0, this.displayedContactsCount);
    },
    showMoreContactsOption() {
      return !this.isSearchMode && this.allContacts.length > this.displayedContactsCount;
    },
    remainingContactsCount() {
      return this.allContacts.length - this.displayedContactsCount;
    },
    // Address field conflict detection
    isBillingAddressMapped() {
      return !!(this.companyMappings['billing_address'] && this.companyMappings['billing_address'] !== '');
    },
    isAnyAddressComponentMapped() {
      const addressComponentKeys = ['address_street', 'address_city', 'address_state', 'address_postcode', 'address_country'];
      return addressComponentKeys.some(key => this.companyMappings[key] && this.companyMappings[key] !== '');
    },
    addressComponentKeys() {
      return ['address_street', 'address_city', 'address_state', 'address_postcode', 'address_country'];
    },
  },
  async mounted() {
    await this.loadData(false); // Load with cache by default
    await this.loadInitialContacts(); // Load first 20 contacts automatically
  },
  methods: {
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
    async loadData(forceRefresh = false) {
      this.loading = true;
      this.error = null;
      
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        // Load ServiceM8 company and contact fields
        await this.loadServiceM8CompanyFields(forceRefresh, headers);
        await this.loadServiceM8ContactFields(forceRefresh, headers);
        
        // Load GHL fields (always fresh)
        await this.loadGHLFields(headers);
        
        // Load field mappings from database
        await this.loadFieldMappings(headers);
        
        
        // Initialize default mappings if no mappings exist
        if (Object.keys(this.companyMappings).length === 0 && Object.keys(this.contactMappings).length === 0) {
          this.initializeDefaultMappings();
        }
        
        // If refreshing and a contact is selected, reload it to get fresh data
        if (forceRefresh && this.selectedContactId) {
          await this.loadExampleContact();
        }
      } catch (error) {
        console.error('Error loading field mapping data:', error);
        this.error = error.message || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    async loadServiceM8CompanyFields(forceRefresh, headers) {
      // Try to load from cache first (unless forcing refresh)
      let fieldData = null;
      if (!forceRefresh) {
        fieldData = this.getCachedFields();
      }
      
      // Fetch fields if not cached or forcing refresh
      if (!fieldData || forceRefresh) {
        const response = await fetch('/servicem8/company-fields', { headers });
        if (response.ok) {
          const data = await response.json();
          fieldData = data.fields || [];
          // Cache the fields
          this.setCachedFields(fieldData);
          console.log('Loaded ServiceM8 company fields from API and cached');
        } else {
          throw new Error('Failed to load ServiceM8 company fields');
        }
      } else {
        console.log('Loaded ServiceM8 company fields from cache');
      }
      
      this.companyFields = fieldData || [];
      console.log(`Displaying ${this.companyFields.length} ServiceM8 company field(s)`);
    },
    async loadServiceM8ContactFields(forceRefresh, headers) {
      // Contact fields are always fresh (no cache needed, they're small)
      const response = await fetch('/servicem8/contact-fields', { headers });
      if (response.ok) {
        const data = await response.json();
        this.contactFields = data.fields || [];
        console.log(`Loaded ${this.contactFields.length} ServiceM8 contact field(s)`);
      } else {
        console.error('Failed to load ServiceM8 contact fields');
        this.contactFields = [];
      }
    },
    async loadGHLFields(headers) {
      try {
        const response = await fetch('/ghl/contact-fields', { headers });
        if (response.ok) {
          const data = await response.json();
          this.ghlFields = data.fields || [];
          console.log(`Loaded ${this.ghlFields.length} GHL contact field(s)`);
        } else {
          console.error('Failed to load GHL contact fields');
          this.ghlFields = [];
        }
      } catch (error) {
        console.error('Error loading GHL contact fields:', error);
        this.ghlFields = [];
      }
    },
    async loadFieldMappings(headers) {
      try {
        // Load company mappings
        const companyResponse = await fetch('/contact-field-mappings?entityType=company', { headers });
        if (companyResponse.ok) {
          const companyMappingsData = await companyResponse.json();
          this.companyMappings = companyMappingsData || {};
          console.log(`Loaded ${Object.keys(this.companyMappings).length} company field mapping(s)`);
          
          // Clean up address field conflicts - prioritize billing_address
          await this.resolveAddressFieldConflicts();
        }
        
        // Load contact mappings
        const contactResponse = await fetch('/contact-field-mappings?entityType=contact', { headers });
        if (contactResponse.ok) {
          const contactMappingsData = await contactResponse.json();
          this.contactMappings = contactMappingsData || {};
          console.log(`Loaded ${Object.keys(this.contactMappings).length} contact field mapping(s)`);
        }
      } catch (error) {
        console.error('Error loading field mappings:', error);
        this.companyMappings = {};
        this.contactMappings = {};
      }
    },
    async resolveAddressFieldConflicts() {
      // Priority: billing_address takes precedence over address component fields
      const billingAddressMapped = !!(this.companyMappings['billing_address'] && this.companyMappings['billing_address'] !== '');
      
      if (billingAddressMapped) {
        // If billing_address is mapped, clear all address component fields
        let hasConflicts = false;
        for (const key of this.addressComponentKeys) {
          if (this.companyMappings[key] && this.companyMappings[key] !== '') {
            hasConflicts = true;
            this.companyMappings[key] = '';
            try {
              await this.deleteFieldMapping(key, 'company');
              console.log(`[Conflict Resolution] Cleared conflicting address component field: ${key}`);
            } catch (error) {
              console.error(`[Conflict Resolution] Failed to clear ${key}:`, error);
            }
          }
        }
        if (hasConflicts) {
          console.log('[Conflict Resolution] Resolved conflicts: billing_address takes priority, cleared address component fields');
        }
      }
    },
    initializeDefaultMappings() {
      // Default company mappings
      const companyDefaults = {
        'name': 'companyName',
        'address_street': 'address1',
        'address_city': 'city',
        'address_state': 'state',
        'address_postcode': 'postalCode',
        'website': 'website',
      };
      
      // Default contact mappings
      const contactDefaults = {
        'first': 'firstName',
        'last': 'lastName',
        'email': 'email',
        'mobile': 'phone',
        'phone': 'phone',
      };
      
      // Only set defaults if they exist in both systems
      Object.keys(companyDefaults).forEach(sm8Key => {
        const sm8Field = this.companyFields.find(f => f.key === sm8Key);
        const ghlKey = companyDefaults[sm8Key];
        const ghlField = this.ghlFields.find(f => f.key === ghlKey);
        
        if (sm8Field && ghlField && !this.companyMappings[sm8Key]) {
          this.companyMappings[sm8Key] = ghlKey;
        }
      });
      
      Object.keys(contactDefaults).forEach(sm8Key => {
        const sm8Field = this.contactFields.find(f => f.key === sm8Key);
        const ghlKey = contactDefaults[sm8Key];
        const ghlField = this.ghlFields.find(f => f.key === ghlKey);
        
        if (sm8Field && ghlField && !this.contactMappings[sm8Key]) {
          this.contactMappings[sm8Key] = ghlKey;
        }
      });
    },
    getCachedFields() {
      try {
        const cached = localStorage.getItem(this.cacheKey);
        if (!cached) return null;
        
        const data = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (data.timestamp && (now - data.timestamp) < this.cacheExpiry) {
          return data.fields;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(this.cacheKey);
          return null;
        }
      } catch (error) {
        console.error('Error reading field cache:', error);
        return null;
      }
    },
    setCachedFields(fields) {
      try {
        const cacheData = {
          fields: fields,
          timestamp: Date.now(),
        };
        localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.error('Error caching fields:', error);
      }
    },
    async handleFieldChange(servicem8FieldKey, event, entityType) {
      const selectedValue = event.target.value;
      const fields = entityType === 'company' ? this.companyFields : this.contactFields;
      const field = fields.find(f => f.key === servicem8FieldKey);
      const fieldType = field ? field.type : 'standard';
      
      try {
        if (selectedValue) {
          // Handle address field conflicts for company fields
          if (entityType === 'company') {
            // Priority: billing_address takes precedence
            // If billing_address is being selected, clear all address component fields
            if (servicem8FieldKey === 'billing_address') {
              await this.clearAddressComponentFields();
              this.companyMappings[servicem8FieldKey] = selectedValue;
            }
            // If any address component is being selected, check if billing_address exists
            // If it does, don't allow the address component to be set (billing_address takes priority)
            else if (this.addressComponentKeys.includes(servicem8FieldKey)) {
              if (this.isBillingAddressMapped) {
                // Billing address is already mapped, don't allow this change
                console.warn(`Cannot map ${servicem8FieldKey}: billing_address is already mapped and takes priority`);
                // Reload mappings to revert the UI
                const ssoToken = await this.getSSOToken();
                const headers = { 'x-sso-token': ssoToken };
                await this.loadFieldMappings(headers);
                return;
              } else {
                // No billing_address, allow the address component to be set
                this.companyMappings[servicem8FieldKey] = selectedValue;
              }
            } else {
              // Not an address field, proceed normally
              this.companyMappings[servicem8FieldKey] = selectedValue;
            }
          } else {
            this.contactMappings[servicem8FieldKey] = selectedValue;
          }
          await this.saveFieldMapping(servicem8FieldKey, selectedValue, fieldType, entityType);
          console.log(`Successfully mapped ${servicem8FieldKey} to ${selectedValue} (${entityType})`);
        } else {
          // Remove mapping from database when changed to "Ignore"
          if (entityType === 'company') {
            this.companyMappings[servicem8FieldKey] = '';
          } else {
            this.contactMappings[servicem8FieldKey] = '';
          }
          await this.deleteFieldMapping(servicem8FieldKey, entityType);
          console.log(`Successfully removed mapping for ${servicem8FieldKey} (${entityType})`);
        }
      } catch (error) {
        // Revert the change if save failed
        console.error('Failed to update field mapping, reverting change:', error);
        // Reload mappings to get the correct state
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        await this.loadFieldMappings(headers);
      }
    },
    async clearAddressComponentFields() {
      // Clear all address component fields
      for (const key of this.addressComponentKeys) {
        if (this.companyMappings[key] && this.companyMappings[key] !== '') {
          this.companyMappings[key] = '';
          await this.deleteFieldMapping(key, 'company');
          console.log(`Cleared address component field: ${key}`);
        }
      }
    },
    async clearBillingAddressField() {
      // Clear billing_address field
      if (this.companyMappings['billing_address'] && this.companyMappings['billing_address'] !== '') {
        this.companyMappings['billing_address'] = '';
        await this.deleteFieldMapping('billing_address', 'company');
        console.log('Cleared billing_address field');
      }
    },
    isFieldDisabled(servicem8FieldKey, entityType) {
      if (entityType !== 'company') return false;
      
      // If billing_address is mapped, disable all address component fields
      if (this.isBillingAddressMapped && this.addressComponentKeys.includes(servicem8FieldKey)) {
        return true;
      }
      
      // If any address component is mapped, disable billing_address
      if (servicem8FieldKey === 'billing_address' && this.isAnyAddressComponentMapped) {
        return true;
      }
      
      return false;
    },
    getFieldDisabledTooltip(servicem8FieldKey, entityType) {
      if (entityType !== 'company') return '';
      
      // If billing_address is mapped, explain why address component fields are disabled
      if (this.isBillingAddressMapped && this.addressComponentKeys.includes(servicem8FieldKey)) {
        return 'This field is disabled because "Billing Address" is already mapped. Billing Address takes priority and includes all address components.';
      }
      
      // If any address component is mapped, explain why billing_address is disabled
      if (servicem8FieldKey === 'billing_address' && this.isAnyAddressComponentMapped) {
        return 'This field is disabled because individual address component fields (Address Street, City, State, Postcode, or Country) are already mapped. Clear those fields first to use Billing Address.';
      }
      
      return '';
    },
    async saveFieldMapping(servicem8FieldKey, ghlFieldKey, fieldType, entityType) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 
          'x-sso-token': ssoToken,
            'Content-Type': 'application/json',
        };
        
        const response = await fetch('/contact-field-mappings', {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            servicem8FieldKey,
            ghlFieldKey,
            fieldType,
            entityType,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to save field mapping');
        }
        
        const mapping = await response.json();
        console.log('Field mapping saved:', mapping);
      } catch (error) {
        console.error('Error saving field mapping:', error);
        throw error;
      }
    },
    async deleteFieldMapping(servicem8FieldKey, entityType) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch(`/contact-field-mappings/${encodeURIComponent(servicem8FieldKey)}?entityType=${entityType}`, {
          method: 'DELETE',
          headers,
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to delete field mapping');
        }
        
        console.log('Field mapping deleted');
      } catch (error) {
        console.error('Error deleting field mapping:', error);
        throw error;
      }
    },
    async loadInitialContacts() {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        const response = await fetch(`/ghl/contacts?limit=20`, { headers });

        if (response.ok) {
          const data = await response.json();
          this.allContacts = data.contacts || [];
          this.displayedContactsCount = 5; // Show first 5
        }
      } catch (error) {
        console.error('Error loading initial contacts:', error);
        this.allContacts = [];
      }
    },
    async searchContacts() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(async () => {
        if (!this.contactSearchTerm || this.contactSearchTerm.length < 2) {
          this.contactSearchResults = [];
        return;
      }

      try {
        const ssoToken = await this.getSSOToken();
          const headers = { 'x-sso-token': ssoToken };
          const response = await fetch(`/ghl/contacts?search=${encodeURIComponent(this.contactSearchTerm)}&limit=20`, { headers });
          
          if (response.ok) {
            const data = await response.json();
            this.contactSearchResults = data.contacts || [];
          }
        } catch (error) {
          console.error('Error searching contacts:', error);
          this.contactSearchResults = [];
        }
      }, 300);
    },
    async loadExampleContact() {
      // Handle special options
      if (this.selectedContactId === '__more__') {
        // Show 5 more contacts
        this.displayedContactsCount = Math.min(this.displayedContactsCount + 5, this.allContacts.length);
        this.selectedContactId = ''; // Reset selection
        return;
      }
      
      if (this.selectedContactId === '__search__') {
        // Enter search mode
        this.isSearchMode = true;
        this.selectedContactId = '';
        return;
      }
      
      if (!this.selectedContactId) {
        this.selectedExampleContact = null;
        return;
      }
      
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        // Add cache-busting timestamp to ensure fresh data
        const timestamp = new Date().getTime();
        const response = await fetch(`/ghl/contacts/${this.selectedContactId}?t=${timestamp}`, { headers });

        if (response.ok) {
          const data = await response.json();
          this.selectedExampleContact = data.contact || data;
          console.log('Loaded example contact:', this.selectedExampleContact);
        } else {
          console.error('Failed to load contact');
          this.selectedExampleContact = null;
        }
      } catch (error) {
        console.error('Error loading example contact:', error);
        this.selectedExampleContact = null;
      }
    },
    exitSearchMode() {
      this.isSearchMode = false;
      this.contactSearchTerm = '';
      this.contactSearchResults = [];
      this.selectedContactId = '';
    },
    clearExampleContact() {
      this.selectedContactId = '';
      this.selectedExampleContact = null;
      this.contactSearchTerm = '';
      this.contactSearchResults = [];
      this.isSearchMode = false;
      this.displayedContactsCount = 5; // Reset to first 5
    },
    formatContactDisplayName(contact) {
      // If companyName is empty, show contactName, otherwise show contactName (companyName)
      if (!contact.companyName || contact.companyName.trim() === '') {
        return contact.name || contact.contactName || 'Unnamed';
      }
      return `${contact.name || contact.contactName || 'Unnamed'} (${contact.companyName})`;
    },
    getExampleValue(servicem8FieldKey, entityType = 'contact') {
      if (!this.selectedExampleContact) {
        console.log('getExampleValue: No selectedExampleContact');
        return '—';
      }
      
      const mappings = entityType === 'company' ? this.companyMappings : this.contactMappings;
      const ghlFieldKey = mappings[servicem8FieldKey];
      
      if (!ghlFieldKey) {
        console.log(`getExampleValue: No mapping for ${servicem8FieldKey}`);
        return '—';
      }
      
      console.log(`getExampleValue: ${servicem8FieldKey} -> ${ghlFieldKey}`);
      
      // Handle formatted phone/mobile fields
      if (ghlFieldKey === 'phone.formatted') {
        return this.getFormattedPhoneValue(this.selectedExampleContact);
      }
      if (ghlFieldKey === 'mobile.formatted') {
        return this.getFormattedMobileValue(this.selectedExampleContact);
      }
      if (ghlFieldKey === 'clientName.formatted') {
        return this.getFormattedClientName(this.selectedExampleContact);
      }
      if (ghlFieldKey === 'address.formatted') {
        return this.getFormattedAddress(this.selectedExampleContact);
      }
      
      // Handle custom fields
      if (ghlFieldKey.startsWith('customFields.')) {
        const customFieldId = ghlFieldKey.replace('customFields.', '');
        const customField = (this.selectedExampleContact.customFields || []).find(cf => cf.id === customFieldId);
        return customField ? (customField.value || '—') : '—';
      }
      
      // Handle standard fields
      const value = this.selectedExampleContact[ghlFieldKey];
      
      // Format the value for display
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      
      // Handle arrays (like tags)
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '—';
      }
      
      // Handle objects (like dndSettings)
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      
      return String(value);
    },
    getFormattedMobileValue(contact) {
      if (!contact) {
        console.log('getFormattedMobileValue: No contact provided');
        return '—';
      }
      
      // Use exact same logic as getMobileNumber() from job mapping
      // Priority: 1. phoneLabel === "Mobile" in contact, 2. First +614 number, 3. null
      
      // First priority: Check main contact phone if phoneLabel is "Mobile"
      if (contact.phone && contact.phoneLabel === 'Mobile') {
        console.log('getFormattedMobileValue: Using main phone with Mobile label:', contact.phone);
        return this.formatPhoneNumberForDisplay(contact.phone);
      }
      
      // Second priority: Check additionalPhones for Mobile
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobilePhone = contact.additionalPhones.find(p => p.phoneLabel === 'Mobile' && p.phone);
        if (mobilePhone && mobilePhone.phone) {
          console.log('getFormattedMobileValue: Using additionalPhones Mobile:', mobilePhone.phone);
          return this.formatPhoneNumberForDisplay(mobilePhone.phone);
        }
      }
      
      // Fallback: Check if main phone starts with +614 (mobile format)
      if (contact.phone && typeof contact.phone === 'string' && contact.phone.startsWith('+614')) {
        console.log('getFormattedMobileValue: Using main phone (detected as mobile):', contact.phone);
        return this.formatPhoneNumberForDisplay(contact.phone);
      }
      
      // Fallback: Check additionalPhones for any +614 number
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const mobileFormatPhone = contact.additionalPhones.find(p => p.phone && p.phone.startsWith('+614'));
        if (mobileFormatPhone && mobileFormatPhone.phone) {
          console.log('getFormattedMobileValue: Using additionalPhones +614:', mobileFormatPhone.phone);
          return this.formatPhoneNumberForDisplay(mobileFormatPhone.phone);
        }
      }
      
      console.log('getFormattedMobileValue: No mobile number found');
      return '—';
    },
    getFormattedPhoneValue(contact) {
      // Use exact same logic as getPhoneNumber() from job mapping
      // Priority: 1. First Home/Work/Landline in additionalPhones, 2. Main phone if not Mobile and not +614, 3. null
      
      const phoneLabels = ['Home', 'Work', 'Landline'];
      
      // First priority: Check additionalPhones for Home/Work/Landline (and not +614)
      if (contact.additionalPhones && Array.isArray(contact.additionalPhones)) {
        const phoneNumber = contact.additionalPhones.find(p => 
          p.phone && phoneLabels.includes(p.phoneLabel) && !p.phone.startsWith('+614')
        );
        if (phoneNumber && phoneNumber.phone) {
          return this.formatPhoneNumberForDisplay(phoneNumber.phone);
        }
      }
      
      // Second priority: Check main contact phone if it's not Mobile and not +614
      if (contact.phone && contact.phoneLabel && phoneLabels.includes(contact.phoneLabel) && !contact.phone.startsWith('+614')) {
        return this.formatPhoneNumberForDisplay(contact.phone);
      }
      
      // Fallback: If main phone is not Mobile and not +614, use main phone
      if (contact.phone && contact.phoneLabel !== 'Mobile' && !contact.phone.startsWith('+614')) {
        return this.formatPhoneNumberForDisplay(contact.phone);
      }
      
      return '—';
    },
    formatPhoneNumberForDisplay(phoneNumber) {
      if (!phoneNumber || typeof phoneNumber !== 'string') return '—';
      
      // Use exact same logic as formatPhoneNumber() from job mapping
      let cleaned = phoneNumber.trim();
      
      // Check if it's a mobile number (starts with +614)
      const isMobileNumber = cleaned.startsWith('+614');
      
      // If it's a mobile number, format as mobile regardless of isMobile parameter
      if (isMobileNumber) {
        // Remove +61 and get digits
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Add 0 prefix and format as 0XXX XXX XXX
          digits = '0' + digits;
          return `${digits[0]}${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
        }
      } else if (cleaned.startsWith('+617')) {
        // Landline number
        let digits = cleaned.replace(/^\+61/, '').replace(/\D/g, '');
        if (digits.length === 9) {
          // Add 0 prefix and format as 0X XXXX XXXX
          digits = '0' + digits;
          return `${digits[0]}${digits[1]} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        }
      }
      
      // Fallback: return cleaned number if format doesn't match
      return cleaned.replace(/^\+61/, '0');
    },
    getFormattedClientName(contact) {
      // Use exact same logic as servicem8.client from job mapping
      // If companyName is not blank, use it; otherwise use firstName + lastName
      if (!contact) return '—';
      
      const companyName = contact.companyName;
      if (companyName && String(companyName).trim() !== '') {
        return String(companyName).trim();
      } else {
        // Concatenate firstName and lastName
        const firstName = contact.firstName || contact.firstNameRaw || '';
        const lastName = contact.lastName || contact.lastNameRaw || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || '—';
      }
    },
    getFormattedAddress(contact) {
      // Use exact same logic as servicem8.jobAddress from job mapping
      if (!contact) return '—';
      
      const companyName = contact.companyName;
      let addressParts = [];
      
      if (companyName && String(companyName).trim() !== '') {
        // Use company address fields
        const address1 = (contact.company && contact.company.address1) || contact.address1 || '';
        const city = (contact.company && contact.company.city) || contact.city || '';
        const state = (contact.company && contact.company.state) || contact.state || '';
        const postcode = (contact.company && contact.company.postcode) || contact.postalCode || contact.postcode || '';
        
        if (address1) addressParts.push(address1);
        const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
        if (cityStatePostcode) addressParts.push(cityStatePostcode);
      } else {
        // Use contact address fields
        const address1 = contact.address1 || '';
        const city = contact.city || '';
        const state = contact.state || '';
        const postcode = contact.postalCode || contact.postcode || '';
        
        if (address1) addressParts.push(address1);
        const cityStatePostcode = [city, state, postcode].filter(v => v).join(' ');
        if (cityStatePostcode) addressParts.push(cityStatePostcode);
      }
      
      // Join with newline
      return addressParts.length > 0 ? addressParts.join('\n') : '—';
    },
    getFieldBadgeLabel(type) {
      if (type === 'standard') return 'Standard';
      if (type === 'custom') return 'Custom';
      if (type === 'reference') return 'Reference';
      return 'Standard';
    },
    getFieldTooltip(fieldKey) {
      if (fieldKey === 'type') {
        return 'Role options: Job, Property Manager, Billing';
      }
      if (fieldKey === 'location_type') {
        return 'Location Type options: Individual, Company, Site';
      }
      if (fieldKey === 'head_office') {
        return 'Head Office: Yes/1 or No/0';
      }
      if (fieldKey === 'is_primary_contact') {
        return 'Primary Contact: Yes/1 or No/0';
      }
      return '';
    },
  },
}
</script>

<style scoped>
.customer-sync-mapping-tab {
  width: 100%;
  padding: 20px;
}

.field-mapping-container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 30px;
}

.section-header h3 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.mapping-section {
  margin-bottom: 40px;
}

.section-title {
  margin-bottom: 16px;
}

.section-title h4 {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.section-subtitle {
  color: #666;
  font-size: 13px;
  margin: 0;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.error-state {
  color: #d32f2f;
}

.btn-retry {
  margin-top: 16px;
  padding: 8px 16px;
  background: #24ACFE;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-retry:hover {
  background: #1a8cd8;
}

.mapping-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.table-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.field-count {
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 12px;
  border-radius: 12px;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 250px;
}

.search-input:focus {
  outline: none;
  border-color: #24ACFE;
}

.btn-refresh {
  padding: 8px 16px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-refresh:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.mapping-table thead {
  background: #f9f9f9;
}

.mapping-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  border-bottom: 2px solid #e0e0e0;
  vertical-align: middle;
}

.mapping-table th:has(+ .sync-direction-cell),
.mapping-table th:nth-child(2) {
  text-align: center;
}

.mapping-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.mapping-table tbody tr:hover {
  background: #f9f9f9;
}

.field-cell {
  width: 25%;
}

.sync-direction-cell {
  width: 10%;
  text-align: center;
  padding: 16px 8px !important;
  vertical-align: middle;
}

.sync-arrow {
  font-size: 32px;
  color: #24ACFE;
  font-weight: 900;
  display: inline-block;
  line-height: 1;
  letter-spacing: 2px;
}

.example-value-cell {
  width: 25%;
}

.example-value {
  font-size: 13px;
  color: #666;
  word-break: break-word;
  max-width: 300px;
  display: inline-block;
  white-space: pre-line; /* Preserve newlines and wrap text */
}

.field-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.field-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.field-type-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.field-type-badge.field-type-standard {
  background: #e3f2fd;
  color: #1976d2;
}

.field-type-badge.field-type-custom {
  background: #f3e5f5;
  color: #7b1fa2;
}

.field-type-badge.field-type-reference {
  background: #fff3e0;
  color: #e65100;
}

.ghl-field-cell {
  width: 40%;
}

.ghl-field-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.ghl-field-select:focus {
  outline: none;
  border-color: #24ACFE;
}

.ghl-field-select.field-disabled,
.ghl-field-select:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
  position: relative;
}

.ghl-field-select.field-disabled:hover,
.ghl-field-select:disabled:hover {
  opacity: 0.8;
}

.ghl-field-select.field-disabled,
.ghl-field-select:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}

.ghl-field-select optgroup {
  font-weight: bold;
  font-size: 14px;
  color: #333;
  background: #f5f5f5;
}

.ghl-field-select optgroup option {
  font-weight: normal;
  padding-left: 20px;
  color: #666;
}

.example-selector {
  background: #f9f9f9;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.example-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
}

.example-selector-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.contact-search-input {
  flex: 1;
  min-width: 200px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.contact-search-input:focus {
  outline: none;
  border-color: #24ACFE;
}

.contact-select {
  flex: 1;
  min-width: 250px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.contact-select:focus {
  outline: none;
  border-color: #24ACFE;
}

.btn-clear-example {
  padding: 8px 16px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.btn-clear-example:hover {
  background: #e0e0e0;
}

.search-input-container {
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 300px;
}

.search-input-container .contact-search-input {
  flex: 1;
  min-width: 200px;
}

.btn-exit-search {
  padding: 8px 16px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.btn-exit-search:hover {
  background: #e0e0e0;
}

.contact-select .more-option {
  font-weight: 600;
  color: #24ACFE;
  background: #f0f8ff;
}

.contact-select .search-option {
  font-weight: 600;
  color: #666;
  background: #f9f9f9;
}

@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .table-header-right {
    width: 100%;
  }
  
  .search-input {
    width: 100%;
  }
  
  .mapping-table {
    font-size: 12px;
  }
  
  .mapping-table th,
  .mapping-table td {
    padding: 12px 8px;
  }
}

</style>
